// A part's Type and Category, as people see them (Mitch, 2026-10-31): Type is Material, Packaging, Sub-assembly or
// Service (the Parts library's tabs and the SKU prefixes MAT- / PAK- / SUB- / SVC-), and one Category whose choices
// follow the Type (materials and sub-assemblies: what it is, which decides its spec's files; packaging: box, sleeve,
// bag…; services: none). No "part type", and no separate packaging "kind", on any screen.
// Stored as before, so planning, BOMs, consignment and costs read exactly what they did: `kind` (how it's sourced and
// planned: material / subassembly / service) and `part_type`; Packaging is kind material + part_type 'packaging', its
// Category in `pack_type`. A packaging part's unit follows its Category (bags, labels…) while nobody has set it.
import { PACK_TYPES, PART_TYPE_LABELS, type PackType, type PartType } from "./projects.ts";

export const ITEM_TYPES = ["material", "packaging", "subassembly", "service"] as const;
export type ItemType = (typeof ITEM_TYPES)[number];
export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  material: "Material",
  packaging: "Packaging",
  subassembly: "Sub-assembly",
  service: "Service",
};

type Typed = { kind: string; part_type?: string | null; pack_type?: string | null };

/** A part's Type ("sell" for a sell item, which has none of these). */
export function typeOf(p: Typed): ItemType | "sell" {
  if (p.kind === "sell") return "sell";
  if (p.part_type === "packaging") return "packaging";
  return (ITEM_TYPES as readonly string[]).includes(p.kind) ? (p.kind as ItemType) : "material";
}
export const typeLabel = (p: Typed) => {
  const t = typeOf(p);
  return t === "sell" ? "Sell item" : ITEM_TYPE_LABELS[t];
};

/** What a material or sub-assembly is, in the order people pick from (it decides the files its spec should have). */
export const PART_CATEGORIES = ["molded", "pcb", "pcba", "electronic", "metal", "fastener", "accessory", "other"] as const satisfies readonly PartType[];
export const PACK_CATEGORY_LABELS: Record<PackType, string> = {
  box: "Box",
  sleeve: "Sleeve",
  bag: "Bag",
  label: "Label",
  insert: "Insert / foam",
  sticker: "Sticker",
  manual: "Manual / card",
  other: "Other",
};

/**
 * Charges --- the Types to choose from: no Service for a new part (what a maker charges is a charge on its BOM now,
 * shared/charges.ts); `current` keeps it for one that's a service already.
 */
export function typeOptions(current?: ItemType | "sell" | ""): { value: ItemType; label: string }[] {
  return ITEM_TYPES.filter((t) => t !== "service" || current === "service").map((t) => ({ value: t, label: ITEM_TYPE_LABELS[t] }));
}

/** The Category choices a Type has: none for a service. */
export function categoryOptions(type: ItemType | "sell"): { value: string; label: string }[] {
  if (type === "packaging") return PACK_TYPES.map((t) => ({ value: t, label: PACK_CATEGORY_LABELS[t] }));
  if (type === "material" || type === "subassembly") return PART_CATEGORIES.map((t) => ({ value: t, label: PART_TYPE_LABELS[t] }));
  return [];
}

/** A part's Category: packaging's kind, else its part type; "" for a service, a sell item or packaging not said yet. */
export function categoryOf(p: Typed): string {
  const t = typeOf(p);
  if (t === "packaging") return p.pack_type ?? "";
  if (t === "material" || t === "subassembly") return p.part_type ?? "other";
  return "";
}
export function categoryLabel(p: Typed): string {
  const value = categoryOf(p);
  return categoryOptions(typeOf(p)).find((o) => o.value === value)?.label ?? "";
}

/** What changing a part's Type writes. A material or sub-assembly keeps its category; one leaving packaging is Other. */
export function typePatch(p: Typed, type: ItemType): { kind: "material" | "subassembly" | "service"; part_type: PartType } {
  if (type === "packaging") return { kind: "material", part_type: "packaging" };
  if (type === "service") return { kind: "service", part_type: "other" };
  const keep = p.part_type && p.part_type !== "packaging" ? (p.part_type as PartType) : "other";
  return { kind: type, part_type: keep };
}

/** What changing a part's Category writes: packaging's kind, else its part type. */
export function categoryPatch(p: Typed, value: string): { pack_type: PackType | "" } | { part_type: PartType } {
  return typeOf(p) === "packaging" ? { pack_type: value as PackType | "" } : { part_type: value as PartType };
}

// Units ---------------------------------------------------------------------------------------------------------------

export const DEFAULT_UNIT = "pcs";
/** A packaging Category's own unit, so stock and the PO read "1,000 bags". */
export const PACK_UNITS: Record<PackType, string> = {
  box: "boxes",
  sleeve: "sleeves",
  bag: "bags",
  label: "labels",
  insert: "inserts",
  sticker: "stickers",
  manual: "manuals",
  other: DEFAULT_UNIT,
};

/** The unit a part of this Type and Category starts with. */
export function unitFor(p: Typed): string {
  return typeOf(p) === "packaging" ? (PACK_UNITS[p.pack_type as PackType] ?? DEFAULT_UNIT) : DEFAULT_UNIT;
}

/** The unit after a part's Type or Category changes: the new one's own, unless someone set a unit of their own (one that
 * isn't what the old Type and Category gave it), which stays. */
export function followUnit(uom: string | null | undefined, before: Typed, after: Typed): string {
  const now = uom ?? "";
  return now === "" || now === unitFor(before) ? unitFor(after) : now;
}
